import React, { useEffect, useState } from 'react';
import {
  fetchRmisAtpConfig,
  runRmisAtpStep,
  type RmisAtpConfig,
  type RmisAtpResult,
} from '../../data/api';
import {
  ATP_CONNECT_KEY,
  ATP_SCREENS,
  ATP_TAG_TYPES,
  defaultValues,
  extractTransferId,
  type AtpStep,
  type Field,
  type TagRow,
  type Values,
} from '../../data/rmisAtp';
import {
  patchAtpSession,
  resetAtpSession,
  useAtpSession,
  type AtpSession,
} from '../../data/rmisAtpStore';

const CARD = 'rounded-2xl border border-[#1e6bb824] bg-white p-4 shadow-sm md:p-6';
const INPUT = 'w-full rounded-lg border border-[#1e6bb833] bg-white px-3 py-2 text-sm text-avcs-text';
const LABEL = 'mb-1 block text-sm font-medium text-avcs-text';
const BTN = 'rounded-lg bg-avcs-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-avcs-primary focus-visible:ring-offset-2';
const BTN_QUIET = 'rounded-lg border border-[#1e6bb833] px-3 py-1.5 text-sm font-medium text-avcs-text transition hover:bg-[#1e6bb80f] focus:outline-none focus-visible:ring-2 focus-visible:ring-avcs-primary';
const ERROR_BOX = 'rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]';
const OK_BOX = 'rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]';
const WARN_BOX = 'rounded-lg border border-[#e7d5b9] bg-[#fbf4e7] px-3 py-2 text-sm text-[#6b4710]';
const CODE = 'overflow-x-auto rounded-lg bg-[#0f1b2d] p-3 text-xs leading-relaxed text-[#d7e3f4]';

const pretty = (value: unknown) => JSON.stringify(value, null, 2);
const isOk = (r?: RmisAtpResult | null) => Boolean(r && r.status_code !== null && r.status_code < 400);

function useAtpConfig() {
  const [config, setConfig] = useState<RmisAtpConfig | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    fetchRmisAtpConfig().then(setConfig).catch(() => setError('Could not load the ATP configuration.'));
  }, []);
  return { config, error };
}

function ConfigGate({ children }: { children: (config: RmisAtpConfig) => React.ReactNode }) {
  const { config, error } = useAtpConfig();
  if (error) return <section className={CARD}><div className={ERROR_BOX}>{error}</div></section>;
  if (!config) return <section className={CARD}><p className="text-sm text-avcs-muted">Loading…</p></section>;
  if (!config.enabled) {
    return (
      <section className={CARD}>
        <h2 className="text-2xl font-semibold text-avcs-text">Gendac ATP</h2>
        <div className={`${WARN_BOX} mt-3`}>The ATP screens are switched off on this server.</div>
      </section>
    );
  }
  return <>{children(config)}</>;
}

function HostBanner({ config }: { config: RmisAtpConfig }) {
  const onAtpHost = /atp/i.test(config.base_url);
  return (
    <div className={onAtpHost ? OK_BOX : WARN_BOX}>
      Talking to <strong>{config.base_url || 'no host configured'}</strong> ({config.environment}).
      {!onAtpHost && ' This is not the Gendac ATP host — fine for a rehearsal, not for the formal run.'}
      {!config.integration_active && ' The RMIS integration is switched off, so calls may fail.'}
    </div>
  );
}

/** GLN + token, shared by every screen. Collapses once the token is in. */
function ConnectionStrip({ session }: { session: AtpSession }) {
  const [editing, setEditing] = useState(!session.token);
  if (!editing && session.token) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#1e6bb824] bg-[#fbfeff] px-4 py-3 text-sm">
        <span>Acting as GLN <strong>{session.gln}</strong> · token entered</span>
        <button type="button" className={BTN_QUIET} onClick={() => setEditing(true)}>Change</button>
      </div>
    );
  }
  return (
    <div className="grid gap-3 rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-4 md:grid-cols-2">
      <label className="block">
        <span className={LABEL}>Our GLN</span>
        <input className={INPUT} value={session.gln} inputMode="numeric" onChange={(e) => patchAtpSession({ gln: e.target.value })} />
      </label>
      <label className="block">
        <span className={LABEL}>Participant token for that GLN</span>
        <input
          className={INPUT} type="password" autoComplete="off" value={session.token}
          placeholder="Held in this page only; never saved" onChange={(e) => patchAtpSession({ token: e.target.value })}
        />
      </label>
      {session.token && (
        <div className="md:col-span-2">
          <button type="button" className={BTN_QUIET} onClick={() => setEditing(false)}>Done</button>
        </div>
      )}
    </div>
  );
}

function TagEditor({ rows, withPrevious, onChange }: { rows: TagRow[]; withPrevious?: boolean; onChange: (rows: TagRow[]) => void }) {
  const set = (i: number, patch: Partial<TagRow>) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded-lg border border-[#1e6bb824] p-2 md:grid-cols-[1fr_9rem_auto]">
          <div className="grid gap-2 md:grid-cols-2">
            <input className={INPUT} aria-label={`Animal ${i + 1} tag`} placeholder="New tag" value={r.tag} onChange={(e) => set(i, { tag: e.target.value })} />
            {withPrevious && (
              <input className={INPUT} aria-label={`Animal ${i + 1} previous tag`} placeholder="Previous tag (if any)" value={r.prevTag} onChange={(e) => set(i, { prevTag: e.target.value })} />
            )}
          </div>
          <div className="grid gap-2">
            <select className={INPUT} aria-label={`Animal ${i + 1} tag type`} value={r.type} onChange={(e) => set(i, { type: e.target.value })}>
              {ATP_TAG_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            {withPrevious && r.prevTag.trim() && (
              <select className={INPUT} aria-label={`Animal ${i + 1} previous tag type`} value={r.prevType} onChange={(e) => set(i, { prevType: e.target.value })}>
                {ATP_TAG_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            )}
          </div>
          <button type="button" className={BTN_QUIET} onClick={() => onChange(rows.filter((_, idx) => idx !== i))} aria-label={`Remove animal ${i + 1}`}>Remove</button>
          {r.tag2 !== undefined ? (
            <div className="grid gap-2 md:col-span-3 md:grid-cols-[1fr_9rem_auto]">
              <input className={INPUT} aria-label={`Animal ${i + 1} second tag`} placeholder="Second new tag" value={r.tag2} onChange={(e) => set(i, { tag2: e.target.value })} />
              <select className={INPUT} aria-label={`Animal ${i + 1} second tag type`} value={r.type2 || 'Manual'} onChange={(e) => set(i, { type2: e.target.value })}>
                {ATP_TAG_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <button type="button" className={BTN_QUIET} onClick={() => set(i, { tag2: undefined, type2: undefined })}>Remove second tag</button>
            </div>
          ) : (
            <div className="md:col-span-3">
              <button type="button" className={BTN_QUIET} onClick={() => set(i, { tag2: '', type2: 'Manual' })}>Add a second tag to this animal</button>
            </div>
          )}
        </div>
      ))}
      <button type="button" className={BTN_QUIET} onClick={() => onChange([...rows, { tag: '', type: 'Manual', prevTag: '', prevType: 'Manual' }])}>
        Add an animal
      </button>
    </div>
  );
}

function FieldInput({ field, value, onChange }: { field: Field; value: Values[string]; onChange: (v: Values[string]) => void }) {
  if (field.type === 'tags') {
    return <TagEditor rows={value as TagRow[]} withPrevious={field.withPrevious} onChange={onChange} />;
  }
  if (field.type === 'select') {
    return (
      <select className={INPUT} value={String(value)} onChange={(e) => onChange(e.target.value)}>
        {(field.options || []).map((o) => <option key={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input
      className={INPUT}
      type={field.type === 'number' ? 'number' : field.type === 'datetime' ? 'datetime-local' : 'text'}
      min={field.type === 'number' ? 0 : undefined}
      value={String(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ResultCard({ step, session }: { step: AtpStep; session: AtpSession }) {
  const entry = session.results[step.id];
  if (!entry) return null;
  const { result, request } = entry;
  const ok = isOk(result);
  const transferId = extractTransferId(result.data);
  const groupId = (result.data as { transferGroupId?: string } | null)?.transferGroupId;
  const rows = step.action === 'query' && Array.isArray(result.data) ? (result.data as Array<Record<string, unknown>>) : [];
  return (
    <div className="space-y-2" aria-live="polite">
      <div className={ok ? OK_BOX : ERROR_BOX}>
        <div className="font-semibold">
          {result.status_code === null
            ? 'Not sent'
            : ok
              ? step.action === 'query' ? `RMIS answered — ${rows.length} transfer${rows.length === 1 ? '' : 's'} found` : 'RMIS accepted this'
              : 'RMIS refused this'}
        </div>
        {result.status_code === null && <div>{result.error}</div>}
        {ok && transferId && step.action !== 'query' && (
          <div className="mt-1">Transfer ID <span className="font-mono">{transferId}</span></div>
        )}
        {ok && step.action === 'group' && groupId && (
          <div className="mt-1">Group ID <span className="font-mono">{groupId}</span></div>
        )}
        {ok && step.action === 'update' && <div className="mt-1">The change is recorded in RMIS.</div>}
        <div className="mt-1 text-xs opacity-75">
          Answer {result.status_code ?? '—'} · request {result.request_id}
        </div>
      </div>
      {rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((row, i) => {
            const id = extractTransferId(row);
            const facts = Object.entries(row)
              .filter(([k]) => /reference|species|sent|status/i.test(k))
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join(' · ');
            return (
              <li key={`${id}-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#1e6bb824] p-2 text-sm">
                <span className="font-mono text-xs">{id.slice(0, 8)}… {facts}</span>
                {id && (
                  <button type="button" className={BTN_QUIET} onClick={() => patchAtpSession({ transferId: id })}>
                    {session.transferId === id ? 'Using this transfer' : 'Use this transfer'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer text-avcs-muted">Show the message sent to RMIS and its answer</summary>
        <div className="mt-2 space-y-2">
          <pre className={CODE}>{pretty(request)}</pre>
          <pre className={CODE}>{pretty(result.data ?? result.error)}</pre>
        </div>
      </details>
    </div>
  );
}

function StepCard({ screenId, step, index, session }: { screenId: string; step: AtpStep; index: number; session: AtpSession }) {
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState('');
  const [showMore, setShowMore] = useState(false);
  const values = session.values[step.id] || defaultValues(step);
  const setValue = (key: string, v: Values[string]) =>
    patchAtpSession((s) => ({ values: { ...s.values, [step.id]: { ...(s.values[step.id] || defaultValues(step)), [key]: v } } }));

  const done = step.kind === 'gendac'
    ? (step.confirm || []).every((_, i) => session.confirmed[`${step.id}:${i}`])
    : isOk(session.results[step.id]?.result);

  const run = async () => {
    setProblem('');
    if (!session.token.trim()) { setProblem('Enter the participant token first (at the top of this page).'); return; }
    const body = step.build!(
      values,
      { gln: session.gln.trim(), transferId: session.transferId, onwardTransferId: session.onwardTransferId },
      new Date(),
    );
    const needsTransfer = step.transferVar === 'transferId' || ('TransferId' in body && !body.TransferId);
    if (needsTransfer && !session.transferId) {
      setProblem('There is no transfer yet — complete the step that creates it first.');
      return;
    }
    setRunning(true);
    let result: RmisAtpResult;
    try {
      result = await runRmisAtpStep({
        action: step.action!,
        participant_token: session.token.trim(),
        ...(step.action === 'query' ? { params: body } : { payload: body }),
        ...(step.action === 'update' ? { transfer_id: session.transferId } : {}),
      });
    } catch {
      setRunning(false);
      setProblem('AVCS could not run this step.');
      return;
    }
    setRunning(false);
    const carriedId = step.captures && isOk(result) ? extractTransferId(result.data) : '';
    patchAtpSession((s) => ({
      results: { ...s.results, [step.id]: { result, request: body } },
      log: [...s.log, {
        at: result.executed_at, screen: screenId, step: step.id, title: step.title, action: step.action!,
        request: body, requestId: result.request_id, statusCode: result.status_code, response: result.data, error: result.error,
      }],
      ...(step.captures && carriedId ? { [step.captures]: carriedId } : {}),
    }));
  };

  const mainFields = (step.fields || []).filter((f) => !f.advanced);
  const moreFields = (step.fields || []).filter((f) => f.advanced);
  const renderField = (f: Field) => (
    <label key={f.key} className={f.type === 'tags' ? 'block md:col-span-2' : 'block'}>
      <span className={LABEL}>{f.label}</span>
      <FieldInput field={f} value={values[f.key] ?? ''} onChange={(v) => setValue(f.key, v)} />
      {f.type === 'datetime' && <span className="mt-1 block text-xs text-avcs-muted">Leave blank to use the time you press the button.</span>}
    </label>
  );

  return (
    <section className={CARD} aria-labelledby={`${step.id}-title`}>
      <div className="flex items-start justify-between gap-3">
        <h3 id={`${step.id}-title`} className="text-lg font-semibold text-avcs-text">
          <span className="mr-2 text-avcs-muted">{index + 1}.</span>{step.title}
          {step.kind === 'gendac' && <span className="ml-2 rounded-full bg-[#1e6bb81a] px-2 py-0.5 text-xs font-medium text-avcs-primary">Gendac</span>}
        </h3>
        {done && <span className="text-sm font-semibold text-[#106b38]">Done</span>}
      </div>
      {step.note && <p className="mt-2 text-sm text-avcs-muted">{step.note}</p>}

      {step.kind === 'call' && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">{mainFields.map(renderField)}</div>
          {moreFields.length > 0 && (
            <div>
              <button type="button" className={BTN_QUIET} aria-expanded={showMore} onClick={() => setShowMore((s) => !s)}>
                {showMore ? 'Hide' : 'More'} details
              </button>
              {showMore && <div className="mt-3 grid gap-4 md:grid-cols-2">{moreFields.map(renderField)}</div>}
            </div>
          )}
          {step.transferVar === 'transferId' && (
            <p className="text-sm text-avcs-muted">Acting on transfer <span className="font-mono">{session.transferId || 'none yet'}</span></p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={BTN} disabled={running} onClick={run}>{running ? 'Working…' : step.button}</button>
            <button type="button" className={BTN_QUIET} onClick={() => patchAtpSession((s) => ({ values: { ...s.values, [step.id]: defaultValues(step) } }))}>
              Reset the form
            </button>
          </div>
          {problem && <div className={ERROR_BOX}>{problem}</div>}
          <ResultCard step={step} session={session} />
        </div>
      )}

      {step.kind === 'gendac' && (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-avcs-muted">Gendac looks at their system and confirms each point:</p>
          {(step.confirm || []).map((line, i) => (
            <label key={line} className="flex items-start gap-2 text-sm text-avcs-text">
              <input
                type="checkbox" className="mt-1" checked={Boolean(session.confirmed[`${step.id}:${i}`])}
                onChange={(e) => patchAtpSession((s) => ({ confirmed: { ...s.confirmed, [`${step.id}:${i}`]: e.target.checked } }))}
              />
              <span>{line}</span>
            </label>
          ))}
          <input
            className={INPUT} placeholder="Notes (optional)" value={session.notes[step.id] || ''}
            onChange={(e) => patchAtpSession((s) => ({ notes: { ...s.notes, [step.id]: e.target.value } }))}
          />
        </div>
      )}
    </section>
  );
}

export function RmisAtpScreen({ screenKey }: { screenKey: string }) {
  const session = useAtpSession();
  const screen = ATP_SCREENS.find((s) => s.key === screenKey) || ATP_SCREENS[0];
  return (
    <ConfigGate>
      {(config) => (
        <div className="mx-auto max-w-4xl space-y-4">
          <section className={CARD}>
            <p className="text-xs font-semibold uppercase tracking-wide text-avcs-muted">Gendac ATP · test {screen.id}</p>
            <h2 className="text-2xl font-semibold text-avcs-text">{screen.title}</h2>
            <p className="mt-1 text-sm text-avcs-muted">{screen.intro}</p>
            <div className="mt-3 space-y-3">
              <HostBanner config={config} />
              <ConnectionStrip session={session} />
            </div>
          </section>
          {screen.steps.map((step, i) => (
            <StepCard key={step.id} screenId={screen.id} step={step} index={i} session={session} />
          ))}
        </div>
      )}
    </ConfigGate>
  );
}

export function RmisAtpConnection() {
  const session = useAtpSession();
  const downloadEvidence = (config: RmisAtpConfig) => {
    const evidence = {
      generated_at: new Date().toISOString(),
      environment: config.environment,
      base_url: config.base_url,
      requesting_gln: session.gln.trim(),
      calls: session.log,
      gendac_confirmations: ATP_SCREENS.flatMap((screen) =>
        screen.steps.filter((s) => s.kind === 'gendac').map((s) => ({
          test: screen.id, step: s.id, title: s.title,
          checks: (s.confirm || []).map((line, i) => ({ text: line, confirmed: Boolean(session.confirmed[`${s.id}:${i}`]) })),
          notes: session.notes[s.id] || '',
        })),
      ),
    };
    const url = URL.createObjectURL(new Blob([pretty(evidence)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `rmis-atp-evidence-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ConfigGate>
      {(config) => (
        <div className="mx-auto max-w-4xl space-y-4">
          <section className={CARD}>
            <p className="text-xs font-semibold uppercase tracking-wide text-avcs-muted">Gendac ATP</p>
            <h2 className="text-2xl font-semibold text-avcs-text">Connection and evidence</h2>
            <p className="mt-1 text-sm text-avcs-muted">
              Each test below is a mock operator screen: key in the values, press the button, and Gendac sees the result in their own system.
              The tags are ATP fixtures — AVCS does not send animal tags in normal use.
            </p>
            <div className="mt-3 space-y-3">
              <HostBanner config={config} />
              <ConnectionStrip session={session} />
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block">
                  <span className={LABEL}>Transfer being worked on</span>
                  <input className={`${INPUT} font-mono`} value={session.transferId} onChange={(e) => patchAtpSession({ transferId: e.target.value })} />
                </label>
                <label className="block">
                  <span className={LABEL}>Onward transfer (8.4)</span>
                  <input className={`${INPUT} font-mono`} value={session.onwardTransferId} onChange={(e) => patchAtpSession({ onwardTransferId: e.target.value })} />
                </label>
              </div>
            </div>
          </section>

          <section className={CARD}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-avcs-text">Evidence log ({session.log.length} calls)</h3>
              <div className="flex gap-2">
                <button type="button" className={BTN} disabled={session.log.length === 0} onClick={() => downloadEvidence(config)}>Download evidence</button>
                <button type="button" className={BTN_QUIET} onClick={() => { if (window.confirm('Clear this session, including the evidence log?')) resetAtpSession(); }}>Start over</button>
              </div>
            </div>
            {session.log.length === 0 ? (
              <p className="mt-2 text-sm text-avcs-muted">Nothing yet. Every button pressed on the test screens is recorded here, with the exact message and RMIS’s answer. The token is never included.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>{['Time', 'Test', 'Step', 'Action', 'Answer'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {session.log.map((entry, i) => (
                      <tr key={`${entry.requestId}-${i}`} className="border-t border-[#1e6bb824]">
                        <td className="px-3 py-2">{new Date(entry.at).toLocaleTimeString()}</td>
                        <td className="px-3 py-2">{entry.screen}</td>
                        <td className="px-3 py-2">{entry.title}</td>
                        <td className="px-3 py-2 uppercase">{entry.action}</td>
                        <td className="px-3 py-2">{entry.statusCode ?? 'not sent'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </ConfigGate>
  );
}

export default function RmisAtpConsole({ view }: { view: string }) {
  return view === ATP_CONNECT_KEY ? <RmisAtpConnection /> : <RmisAtpScreen screenKey={view} />;
}
